import React from 'react';
import { Link } from 'react-router-dom';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { ReactionPayload } from '../../../generated-api';

interface ReactionCardProps {
  payload: ReactionPayload;
  createdAt: string;
}

const EMOJI_MAP: Record<string, string> = {
  heart: '\u2764\uFE0F',
  trophy: '\uD83C\uDFC6',
  fire: '\uD83D\uDD25',
  mad: '\uD83D\uDE21',
  devil: '\uD83D\uDE08',
};

export const ReactionCard: React.FC<ReactionCardProps> = ({ payload, createdAt }) => {
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const emojiDisplay = EMOJI_MAP[payload.emoji] || payload.emoji;

  return (
    <Link
      data-track="feed_reaction_card"
      to={`/plays/${payload.playId}`}
      className="block bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-yellow-100 rounded-full p-2">
              <FaceSmileIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">New Reaction</p>
              <p className="text-xs text-muted">{formatRelativeTime(createdAt)}</p>
            </div>
          </div>
          <span className="text-2xl">{emojiDisplay}</span>
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            <span className="font-medium text-yellow-700">{payload.playerName}</span>
            {' reacted with '}
            <span className="text-lg">{emojiDisplay}</span>
            {' to a play of '}
            <span className="font-medium">{payload.gameName}</span>
          </p>
        </div>
      </div>
    </Link>
  );
};
